// frontend/src/utils/OnboardingFlow.js

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { acceptTerms } from './onboardingService';
import { cloneInitialData } from './accountCloning';
import { fetchDocumentTypes } from './fetchDocumentTypes';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import { CircleCheck, FileText, MessagesSquare, Network, X } from 'lucide-react';
import SafeHtml from '../components/common/SafeHtml';

const TERMS_VERSION = '1.0';
const ONBOARDING_CARD_COUNT = 3;

const STEPS = {
  WELCOME: 'welcome',
  CLINICAL_MEMORY: 'clinical-memory',
  TERMS: 'terms',
  SETUP: 'setup',
  COMPLETE: 'complete'
};

// Keep the component identity stable when session polling refreshes the parent.
// Defining this inside OnboardingFlow remounted it and lost the reviewed scroll
// position, disabling acceptance while the user was reading the agreement.
const TermsStep = ({ header, onAccept }) => {
  const [termsContent, setTermsContent] = useState('');
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);

  useEffect(() => {
    let active = true;
    fetch('/legal/terms.html')
      .then(response => response.text())
      .then(html => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        doc.body.querySelectorAll('script, style').forEach(el => el.remove());
        if (active) setTermsContent(doc.body.innerHTML);
      })
      .catch(err => console.error('Failed to load terms:', err));
    return () => { active = false; };
  }, []);

  const handleScroll = (e) => {
    const element = e.target;
    if (element.scrollHeight - element.scrollTop <= element.clientHeight + 50) {
      setHasScrolledToBottom(true);
    }
  };

  return (
    <div className="onboarding-container onboarding-container-refreshed">
      <div className="onboarding-card onboarding-card-terms onboarding-card-refreshed">
        {header}
        <h4 className="onboarding-title">Terms and Conditions</h4>
        <p className="onboarding-subtitle">Please review and accept to continue</p>
        <SafeHtml className="onboarding-terms-body" onScroll={handleScroll} html={termsContent} />
        <div className="onboarding-terms-footer">
          <p className="terms-scroll-notice">
            {hasScrolledToBottom ? 'You can now accept the terms.' : 'Please scroll to the bottom to accept'}
          </p>
          <div className="onboarding-button-container">
            <button onClick={onAccept} className="center-button primary" disabled={!hasScrolledToBottom}>
              I Accept
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const OnboardingFlow = ({
  user,
  onComplete,
  previewMode = false,
  aiAssistantAvailable = false,
  commentsAvailable = false,
  onExitPreview,
  hasAcceptedTerms = false,
  hasClonedData = false,
  userSettings = null,
}) => {
  const [currentStep, setCurrentStep] = useState(
    !previewMode && hasAcceptedTerms && hasClonedData === false
      ? STEPS.SETUP
      : STEPS.WELCOME
  );
  const [error, setError] = useState('');
  const [isSetupRunning, setIsSetupRunning] = useState(false);
  const setupAttemptedRef = useRef(false);
  const [samplePatientId, setSamplePatientId] = useState(null);
  const [summaryAvailable, setSummaryAvailable] = useState(false);

  // Setup may finish in another tab. Reconcile a stale welcome/Terms screen
  // with the confirmed account flags without repeating acceptance or cloning.
  useEffect(() => {
    if (previewMode || !hasAcceptedTerms || currentStep === STEPS.SETUP || currentStep === STEPS.COMPLETE) return;
    if (hasClonedData === true) setCurrentStep(STEPS.COMPLETE);
    else if (hasClonedData === false) setCurrentStep(STEPS.SETUP);
  }, [previewMode, hasAcceptedTerms, hasClonedData, currentStep]);

  useEffect(() => {
    if (currentStep !== STEPS.COMPLETE || !user?.uid) return undefined;
    let active = true;
    setSummaryAvailable(false);
    // Use the same company policy and personal visibility filters as Generate Summary.
    fetchDocumentTypes(types => {
      if (active) setSummaryAvailable(types.some(type => type.format === 'summary'));
    }, undefined, undefined, undefined, undefined, null, userSettings);
    return () => { active = false; };
  }, [currentStep, user?.uid, userSettings]);

  const runSetup = useCallback(async () => {
    if (previewMode || !user?.uid || isSetupRunning) return;

    setError('');
    setIsSetupRunning(true);

    try {
      const result = await cloneInitialData();
      if (result?.failedCloneCount > 0 || result?.successfulCloneCount < 1) {
        throw new Error('Sample patient setup did not complete');
      }

      setSamplePatientId(result.patientIds?.[0] || null);
      setCurrentStep(STEPS.COMPLETE);
    } catch (err) {
      console.error('Onboarding setup error:', err);
      setError('Failed to set up your account. Please try again.');
    } finally {
      setIsSetupRunning(false);
    }
  }, [isSetupRunning, previewMode, user?.uid]);

  useEffect(() => {
    if (
      currentStep === STEPS.SETUP
      && !previewMode
      && !setupAttemptedRef.current
    ) {
      setupAttemptedRef.current = true;
      runSetup();
    }
  }, [currentStep, previewMode, runSetup]);

const OnboardingHeader = ({ currentCard }) => (
  <div className="onboarding-progress-header">
    <div
      className="onboarding-card-progress"
      role="progressbar"
      aria-label="Onboarding progress"
      aria-valuemin="1"
      aria-valuemax={ONBOARDING_CARD_COUNT}
      aria-valuenow={currentCard}
    >
      {Array.from({ length: ONBOARDING_CARD_COUNT }, (_, index) => {
        const card = index + 1;
        const stateClass = card === currentCard
          ? ' is-active'
          : card < currentCard
            ? ' is-complete'
            : '';

        return (
          <span
            key={card}
            className={`onboarding-progress-dot${stateClass}`}
            aria-hidden="true"
          />
        );
      })}
    </div>
    {previewMode && (
      <Tooltip title="Exit preview" arrow>
        <button
          type="button"
          className="onboarding-preview-exit"
          aria-label="Exit preview"
          onClick={onExitPreview || onComplete}
        >
          <X size={16} aria-hidden="true" />
        </button>
      </Tooltip>
    )}
  </div>
);

// === Welcome Step ===
const WelcomeStep = () => (
  <div className="onboarding-container onboarding-container-refreshed">
    <div className="onboarding-card onboarding-card-refreshed">
      <OnboardingHeader currentCard={1} />
      <h1 className="onboarding-title">Get your time back.</h1>
      <p className="onboarding-text">
        Featherglass turns your sessions and outside records into finished clinical
        documentation. Your attention stays on the patient.
      </p>

      <div className="onboarding-button-container">
        <button
          onClick={() => setCurrentStep(STEPS.CLINICAL_MEMORY)}
          className="center-button primary"
        >
          Next
        </button>
      </div>
    </div>
  </div>
);

const ClinicalMemoryStep = () => (
  <div className="onboarding-container onboarding-container-refreshed">
    <div className="onboarding-card onboarding-card-refreshed">
      <OnboardingHeader currentCard={2} />
      <h1 className="onboarding-title">More than notes. A clinical memory.</h1>
      <p className="onboarding-text">
        One connected picture, built from recorded sessions and outside records,
        so you never start a session blind.
      </p>

      <div className="onboarding-button-container">
        <button
          onClick={() => setCurrentStep(STEPS.TERMS)}
          className="center-button primary"
        >
          Review terms
        </button>
      </div>
    </div>
  </div>
);

  // === Terms Step ===
  const handleTermsAccept = async () => {

    setError('');

    if (previewMode) {
      setCurrentStep(STEPS.SETUP);
      return;
    }

    try {
      // Save T&C acceptance
      await acceptTerms(TERMS_VERSION);

      // Terms evidence is independent from sample setup. Moving to this step
      // triggers the same resumable setup path used after a refresh.
      setupAttemptedRef.current = false;
      setCurrentStep(STEPS.SETUP);
    } catch (err) {
      console.error('Onboarding terms error:', err);
      setError('Failed to accept the terms. Please try again.');
    }
  };

  // === Setup Step ===
  const SetupStep = () => (
    <div className="onboarding-container onboarding-container-refreshed">
      <div className="onboarding-card onboarding-card-refreshed">
        <OnboardingHeader currentCard={2} />
        <h4 className="onboarding-title">Setting up your account...</h4>
        
        {isSetupRunning && (
          <div className="onboarding-progress">
            <CircularProgress size={60} />
          </div>
        )}

        <div className="onboarding-status">
          <p>Creating sample patient</p>
          <p>Preparing your workspace</p>
          <p className="onboarding-wait">This will only take a moment</p>
        </div>

        {error && <p className="error-message">{error}</p>}

        {error && !previewMode && !isSetupRunning && (
          <div className="onboarding-button-container">
            <button
              type="button"
              onClick={runSetup}
              className="center-button primary"
            >
              Try again
            </button>
          </div>
        )}

        {previewMode && (
          <div className="onboarding-button-container">
            <button
              onClick={() => setCurrentStep(STEPS.COMPLETE)}
              className="center-button primary"
            >
              Continue preview
            </button>
          </div>
        )}
      </div>
    </div>
  );

  // === Complete Step ===
const CompleteStep = () => (
  <div className="onboarding-container onboarding-container-refreshed">
    <div className="onboarding-card onboarding-card-refreshed onboarding-card-capabilities">
      <OnboardingHeader currentCard={3} />
      <div className="onboarding-success-icon">
        <CircleCheck size={64} color="var(--color-success)" aria-hidden="true" />
      </div>

      <h1 className="onboarding-title">You're All Set!</h1>
      <p className="onboarding-subtitle">
        Your workspace is ready, with a sample patient to explore.
      </p>
      <p className="onboarding-text">
        {summaryAvailable
          ? (aiAssistantAvailable
            ? 'Try generating a treatment summary, or ask the record a question.'
            : 'Try generating a treatment summary.')
          : (aiAssistantAvailable
            ? 'Explore the sample note, or ask the record a question.'
            : 'Explore the sample note to see how your workspace works.')}
      </p>

      <h2 className="onboarding-capabilities-title">Here's what that unlocks.</h2>
      <div className="onboarding-capabilities-list">
        <div className="onboarding-capability">
          <FileText aria-hidden="true" />
          <span>{summaryAvailable
            ? 'A complete treatment summary in one click, anytime you need one.'
            : 'Your notes and patient history together in one place.'}</span>
        </div>
        <div className="onboarding-capability">
          <MessagesSquare aria-hidden="true" />
          <span>{commentsAvailable
            ? 'Comment and share right on the record, with colleagues in your organization.'
            : 'Share patient records with colleagues in your organization.'}</span>
        </div>
        <div className="onboarding-capability">
          <Network aria-hidden="true" />
          <span>The whole treatment team on one living record.</span>
        </div>
      </div>

      <p className="onboarding-workspace-positioning">
        One shared, intelligent workspace, sitting above your EHR.
      </p>

      <div className="onboarding-button-container onboarding-completion-actions">
        <button onClick={() => onComplete({ patientId: samplePatientId, exploreSample: true })} className="center-button primary">
          Explore sample patient
        </button>
        <button onClick={() => onComplete()} className="center-button secondary">
          Open my workspace
        </button>
      </div>
    </div>
  </div>
);

  // === Main Render ===
  return (
    <>
      {currentStep === STEPS.WELCOME && <WelcomeStep />}
      {currentStep === STEPS.CLINICAL_MEMORY && <ClinicalMemoryStep />}
      {currentStep === STEPS.TERMS && <TermsStep header={<OnboardingHeader currentCard={2} />} onAccept={handleTermsAccept} />}
      {currentStep === STEPS.SETUP && <SetupStep />}
      {currentStep === STEPS.COMPLETE && <CompleteStep />}
    </>
  );
};

export default OnboardingFlow;
